import axios from 'axios';
import Store from '~/redux/store';
import { clearStorage } from './local-storage';
import {
  getUserToken,
  getRefreshToken,
  setApiKeys,
  getImpersonationMode,
  getAdminToken,
} from './auth';
import {
  SET_LIMIT_MODAL,
  SET_DYNAMIC_LIMIT_MODAL,
  SET_PAYMENT_FAILED_MODAL,
  CLEAR_PAYMENT_FAILED_MODAL,
  SET_2FA_REVERIFICATION_REQUIRED,
} from '~/redux/actions/action-types';
import { failure } from '~/redux/actions/snackbar-actions';
import { MESSAGES } from '~/utils/message';
import { isOfferGeneratorWebsite } from './helperFunctions';

// ─── Module-level flag ────────────────────────────────────────────────────────
// Set synchronously the moment the first payment-failed response arrives.
// Because it's a plain JS variable (not React/Redux state), it updates
// instantly — preventing all concurrent API calls from each dispatching
// their own SET_PAYMENT_FAILED_MODAL action before Redux state has a chance
// to reflect the first dispatch.
let _paymentFailedDispatched = false;

/**
 * Call this when the payment-failed modal is dismissed so the guard resets
 * and can fire again on the next payment failure.
 */
export const resetPaymentFailedFlag = () => {
  _paymentFailedDispatched = false;
};

const isPaymentFailedModalOpen = () => {
  return Store.getState()?.paymentFailureReducer?.isOpen === true;
};

const isStripeSetupIntentRequest = (url = '') => {
  return String(url || '').includes('/stripe/setup/intent/create');
};

export const getPaymentFailedAtHeader = (headers) => {
  if (!headers) return null;

  if (typeof headers.get === 'function') {
    return headers.get('x-payment-failed-at');
  }

  const paymentFailedHeaderKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === 'x-payment-failed-at'
  );

  return paymentFailedHeaderKey ? headers[paymentFailedHeaderKey] : null;
};

const clearPaymentFailedModalIfNeeded = (url = '') => {
  if (isStripeSetupIntentRequest(url)) {
    return;
  }

  if (_paymentFailedDispatched || isPaymentFailedModalOpen()) {
    Store.dispatch({ type: CLEAR_PAYMENT_FAILED_MODAL });
  }
};

const apiURL = `${
  isOfferGeneratorWebsite()
    ? import.meta.env.VITE_APP_OFFER_GENERATOR_BASE_URL
    : import.meta.env.VITE_APP_BASE_URL
}/api/v1/${isOfferGeneratorWebsite() ? 'sdk/' : ''}`;
const token = () => `Bearer ${getUserToken() || ''}`;
const refreshToken = () => `${getRefreshToken() || ''}`;
const adminToken = () => `Bearer ${getAdminToken() || ''}`;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

const request = {
  post: (endpoint, payload, config) => {
    const instance = axios.create({
      apiURL,
      headers: {
        Authorization: token(),
        refreshtoken: refreshToken(),
        ...(getImpersonationMode() ? { 'X-JWT': adminToken() } : undefined),
        'X-HubSpot-UTK': getCookie('hubspotutk'),
      },
    });
    requestInterceptors(instance);
    return instance.post(apiURL + endpoint, payload, config);
  },
  get: (endpoint, payload, signal) => {
    const instance = axios.create({
      apiURL,
      headers: {
        Authorization: token(),
        refreshtoken: refreshToken(),
        ...(getImpersonationMode() ? { 'X-JWT': adminToken() } : undefined),
      },
      params: payload,
    });
    requestInterceptors(instance);
    return instance.get(apiURL + endpoint, signal ? { signal } : undefined);
  },
  getOne: (endpoint, id) => {
    return this.get(endpoint, { id });
  },
  put: (endpoint, payload) => {
    const instance = axios.create({
      apiURL,
      headers: {
        Authorization: token(),
        refreshtoken: refreshToken(),
        ...(getImpersonationMode() ? { 'X-JWT': adminToken() } : undefined),
      },
    });
    requestInterceptors(instance);
    return instance.put(apiURL + endpoint, payload);
  },
  patch: (endpoint, payload) => {
    const instance = axios.create({
      apiURL,
      headers: {
        Authorization: token(),
        refreshtoken: refreshToken(),
        ...(getImpersonationMode() ? { 'X-JWT': adminToken() } : undefined),
      },
    });
    requestInterceptors(instance);
    return instance.patch(apiURL + endpoint, payload);
  },
  delete: (endpoint, payload, config) => {
    const instance = axios.create({
      apiURL,
      headers: {
        Authorization: token(),
        refreshtoken: refreshToken(),
        ...(getImpersonationMode() ? { 'X-JWT': adminToken() } : undefined),
      },
      params: payload,
    });
    requestInterceptors(instance);
    return instance.delete(apiURL + endpoint, config);
  },
};

// For handling all types of http requests/response
const requestInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return config;
  });

  instance.interceptors.response.use(
    function (response) {
      // Update token in localStorage
      if (response?.headers?.token && response?.headers?.refreshtoken) {
        setApiKeys(response.headers.token, response.headers.refreshtoken);
      }
      const paymentFailedAt = getPaymentFailedAtHeader(response?.headers);

      if (paymentFailedAt) {
        if (!_paymentFailedDispatched) {
          _paymentFailedDispatched = true; // set synchronously - blocks all concurrent calls
          Store.dispatch({ type: SET_PAYMENT_FAILED_MODAL });
        }
      } else {
        clearPaymentFailedModalIfNeeded(response?.config?.url);
      }

      return response;
    },
    function (error) {
      // Also check for X-Payment-Failed-At on error responses (403, etc.)
      // NOTE: Axios stores error response headers as a plain object OR as an
      // AxiosHeaders instance. We use getHeaders() / direct access + fallback.
      const errorPaymentFailedAt = getPaymentFailedAtHeader(error?.response?.headers);

      if (errorPaymentFailedAt) {
        if (!_paymentFailedDispatched) {
          _paymentFailedDispatched = true;
          Store.dispatch({ type: SET_PAYMENT_FAILED_MODAL });
        }
      } else if (error?.response) {
        clearPaymentFailedModalIfNeeded(error.response.config?.url);
      }

      if (
        error?.response?.status === 403 &&
        error?.response?.data?.data?.code === '2FA_REVERIFICATION_REQUIRED'
      ) {
        Store.dispatch({
          type: SET_2FA_REVERIFICATION_REQUIRED,
          payload: { email: error.response.data.data.email },
        });
        return Promise.reject(error);
      }

      if (error?.response?.status === 401) {
        // handle error: inform user, go to login, etc
        clearStorage();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        Store.dispatch(failure(error?.response?.data?.message || MESSAGES.GENERAL_ERROR));
      } else if (
        error?.response?.config?.url?.includes('/contacts/bulk-actions') &&
        error?.response?.status === 418
      ) {
        setTimeout(
          () =>
            Store.dispatch({
              type: SET_DYNAMIC_LIMIT_MODAL,
              payload: {
                status: true,
                title: MESSAGES.REQUEST.SUBSCRIPTION_PLAN_DENIED.TITLE,
                subHeading: MESSAGES.REQUEST.SUBSCRIPTION_PLAN_DENIED.SUBHEADING,
                message: MESSAGES.REQUEST.SUBSCRIPTION_PLAN_DENIED.MESSAGE,
              },
            }),
          500
        );
        error.response.data.message = null;
        return Promise.reject(error);
      } else if (
        error?.response?.config?.url?.includes('/api-keys') &&
        error?.response?.status === 418
      ) {
        const repsonseError = error?.response?.data?.message || MESSAGES.GENERAL_ERROR;
        setTimeout(
          () =>
            Store.dispatch({
              type: SET_DYNAMIC_LIMIT_MODAL,
              payload: {
                status: true,
                title: '',
                subHeading: '',
                message: repsonseError,
              },
            }),
          500
        );
        error.response.data.message = null;
        return Promise.reject(error);
      } else if (
        (error?.response?.config?.url?.includes('/users') ||
          error?.response?.config?.url?.includes('/custom-fields') ||
          error?.response?.config?.url?.includes('/tags')) &&
        error?.response?.status === 418
      ) {
        setTimeout(() => Store.dispatch({ type: SET_LIMIT_MODAL, payload: true }), 500);

        try {
          const parsed = JSON.parse(error?.response?.request?.response);
          error.response.data.message = parsed?.message || null;
        } catch (e) {
          error.response.data.message =
            'You have reached your limit. Upgrade your plan to add more records.';
        }
        return Promise.reject(error);
      } else {
        return Promise.reject(error);
      }
    }
  );
};

export default request;
